import { ChevronDown } from "lucide-react";
import { FC, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PaginationInfo } from "@/data/types";

type Props = {
  paginationInfo?: PaginationInfo;
};

export const TableSizeDropdown: FC<Props> = ({ paginationInfo }) => {
  const [searchParams] = useSearchParams();

  const pagination = useMemo(
    () => ({
      pageIndex: Number(searchParams.get("page")) || 1,
      pageSize: Number(searchParams.get("limit")) || 20,
    }),
    [searchParams],
  );

  const generateLink = useCallback(
    (size: number) => {
      const params = new URLSearchParams(searchParams);
      const currentPage = pagination.pageIndex;
      const totalItems = paginationInfo?.count || 0;

      params.set("limit", size.toString());

      // If page is set and will be invalid with new limit, set to last page
      if (
        searchParams.has("page") &&
        currentPage > Math.ceil(totalItems / size)
      ) {
        params.set("page", Math.ceil(totalItems / size).toString());
      }

      return `?${params.toString()}`;
    },
    [searchParams, pagination.pageIndex, paginationInfo?.count],
  );

  return (
    <div className="flex-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            {searchParams.get("limit") ?? 20} per page <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {[20, 50, 100, 200, 500, 1000].map((size) => {
            return (
              <Link
                key={size.toString()}
                to={generateLink(size)}
                preventScrollReset
              >
                <DropdownMenuCheckboxItem
                  key={size.toString()}
                  className="capitalize"
                  checked={Number(searchParams.get("limit")) === size}
                >
                  {size} per page
                </DropdownMenuCheckboxItem>
              </Link>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
